import { FC, useEffect } from 'react';
import { MessageMediumTab } from 'modules/intelligentInsights/frequencyOptimization/types';
import { ColumnData } from 'ui/tableHeader';
import {
  MessageTypesTable,
  MessageTypesTableHeader,
  MessageTypesTableRows,
  MessageTypeUnsubscribeHeader,
  StyledPageFooter,
} from 'modules/intelligentInsights/frequencyOptimization/components/breakdown/messageType/BreakdownMessageTypeTable.styles';
import { useFroInsightsCoreContext } from 'modules/intelligentInsights/frequencyOptimization/contexts/CoreContext';
import {
  getMessageTypesByMedium,
  engagementMetricsByMedium,
} from 'modules/intelligentInsights/frequencyOptimization/components/breakdown/breakdownUtils';
import { useClientPaginatedState } from 'ui/table/hooks/useClientPaginatedState';
import { BreakdownMessageTypeLoading } from 'modules/intelligentInsights/frequencyOptimization/components/breakdown/messageType/BreakdownMessageTypeLoading';
import {
  BREAKDOWN_AUDIENCE_MESSAGE_TYPES_MAX,
  BREAKDOWN_MESSAGE_TYPE_UNSUBSCRIBE_TOOLTIP,
} from 'modules/intelligentInsights/constants';
import { ConfigByMedium } from 'modules/frequencyManagement/lib/types';
import { useMessageTypeCampaignDataQuery } from 'modules/intelligentInsights/frequencyOptimization/components/breakdown/messageType/hooks/useMessageTypeCampaignData';
import {
  MessageTypeClickRateCell,
  MessageTypeNameCell,
  MessageTypeOpenRateCell,
  MessageTypeRowCell,
  MessageTypeSkippedCell,
  MessageTypeUnsubRateCell,
} from 'modules/intelligentInsights/frequencyOptimization/components/breakdown/messageType/BreakdownMessageTypeCells';
import { BreakdownMessageTypeError } from 'modules/intelligentInsights/frequencyOptimization/components/breakdown/messageType/BreakdownMessageTypeError';
import { HelpTooltip } from 'ui/tooltip';
import { colors } from 'ui/theme';

export type MessageTypeColumnData = ColumnData & {
  RowCell: MessageTypeRowCell;
};

export const MESSAGE_TYPE_COLUMNS: Array<MessageTypeColumnData> = [
  {
    align: 'flex-start',
    name: 'Message type',
    value: 'name',
    sortable: false,
    RowCell: MessageTypeNameCell,
    width: 540,
  },
  {
    align: 'flex-start',
    name: 'Open rate lift',
    value: 'openRateLift',
    sortable: true,
    RowCell: MessageTypeOpenRateCell,
  },
  {
    align: 'flex-start',
    name: 'Click rate lift',
    value: 'clickRateLift',
    sortable: true,
    RowCell: MessageTypeClickRateCell,
  },
  {
    align: 'flex-start',
    name: (
      <MessageTypeUnsubscribeHeader>
        Unsubscribe rate lift
        <HelpTooltip
          tooltipContent={BREAKDOWN_MESSAGE_TYPE_UNSUBSCRIBE_TOOLTIP}
          iconProps={{ width: 16, height: 16, color: colors.iconPrimary }}
        />
      </MessageTypeUnsubscribeHeader>
    ),
    value: 'unsubscribeRateLift',
    sortable: true,
    RowCell: MessageTypeUnsubRateCell,
  },
  {
    align: 'flex-start',
    name: '% Messages skipped',
    value: 'messagesSkipped',
    sortable: true,
    RowCell: MessageTypeSkippedCell,
  },
];

export const filterColumnsByMedium = (
  columns: Array<MessageTypeColumnData>,
  messageMedium: MessageMediumTab,
) => {
  const allMediumCols = ['name']
    .concat(engagementMetricsByMedium[messageMedium].metricKeys as string[])
    .concat(['messagesSkipped']);

  return allMediumCols.map((col) => {
    return columns.find((column) => column.value === col) as MessageTypeColumnData;
  });
};

export const BREAKDOWN_MESSAGE_TYPE_TABLE_TEST_ID = 'breakdown-message-type-table';

export type BreakdownMessageTypeTableProps = {
  messageMedium: MessageMediumTab;
  frmSettings: ConfigByMedium;
};

export const BreakdownMessageTypeTable: FC<BreakdownMessageTypeTableProps> = ({
  frmSettings,
  messageMedium,
}) => {
  const {
    froInsightsData,
    isLoading: isFroDataLoading,
    isError: isFroDataError,
  } = useFroInsightsCoreContext();
  const rawMessageTypeRows = getMessageTypesByMedium(froInsightsData, frmSettings, messageMedium);
  const {
    data: campaignEnhancedMessageTypeRows,
    isLoading: isMessageTypesDataLoading,
    isError: isMessageTypesDataError,
  } = useMessageTypeCampaignDataQuery(
    rawMessageTypeRows,
    // These should always indicate campaign loading given FrO loading is handled higher up in the tree
    isFroDataLoading,
    isFroDataError,
  );

  const {
    currentPage,
    itemsPerPage,
    numItems,
    rows,
    setCurrentPage,
    setSortDirection,
    setSortedBy,
    sortDirection,
    sortedBy,
    setItemsPerPage,
  } = useClientPaginatedState({
    rows: campaignEnhancedMessageTypeRows,
    sortedBy: MESSAGE_TYPE_COLUMNS[0].value,
  });

  const mediumCols = filterColumnsByMedium(MESSAGE_TYPE_COLUMNS, messageMedium);

  useEffect(() => {
    setItemsPerPage(BREAKDOWN_AUDIENCE_MESSAGE_TYPES_MAX);
  }, []);

  if (isMessageTypesDataLoading) {
    return <BreakdownMessageTypeLoading />;
  }

  if (isMessageTypesDataError) {
    return <BreakdownMessageTypeError onRefresh={() => window.location.reload()} />;
  }

  return (
    <div>
      <MessageTypesTable id={BREAKDOWN_MESSAGE_TYPE_TABLE_TEST_ID}>
        <MessageTypesTableHeader
          columns={mediumCols}
          setSortDirection={setSortDirection}
          setSortedBy={setSortedBy}
          sortDirection={sortDirection}
          sortedBy={sortedBy}
        />
        <MessageTypesTableRows idKey="messageTypeId" columns={mediumCols} rows={rows} />
      </MessageTypesTable>
      <StyledPageFooter
        currentPage={currentPage}
        itemsPerPage={itemsPerPage}
        numItems={numItems}
        onUpdate={({ page }) => {
          setCurrentPage(page);
        }}
        maxPages={Math.ceil(numItems / itemsPerPage)}
        pageSizes={[BREAKDOWN_AUDIENCE_MESSAGE_TYPES_MAX]}
      />
    </div>
  );
};
